// CTV-787: Station settings, the publisher admin's one place for a station's
// configuration. Consolidates what were two surfaces (the Team sidebar item,
// and the payout-method setup CTV-776 had put on the Billing tab) into one
// Settings item at /admin/:slug/settings with an in-page tab strip, on the
// model of the advertiser's Settings surface at /:slug/settings.
//
// Station-bound in the CTV-759 sense: it WRITES CONFIGURATION to one station,
// so the scope selector never offers All stations and /admin/all/settings/...
// collapses in AdminShell like any other station-bound path. Billing stays
// scope-flexible because it READS ROWS.
//
// The layout owns the single AdminPageHeader and the manager+ gate for both
// tabs, so each tab component is a plain sections-only page. The API gates
// every route underneath on its own; hiding the item is a courtesy.

import React from 'react';
import { Navigate, NavLink, Outlet } from 'react-router-dom';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { usePayoutSetupStatus } from '../../components/self-service/admin/usePayoutSetupStatus';
import { scopeForContext } from '../../components/self-service/admin/stationScopeRequest';
import { publisherBillingEnabled } from '../../components/self-service/admin/payoutsFeature';
import { rankOf } from '../../components/self-service/shared/roleRank';
import { displayedName } from '../../data/publisherConfigs';

const tabClass = ({ isActive }: { isActive: boolean }) =>
  [
    'inline-flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors',
    isActive
      ? 'text-slate-900 border-slate-900'
      : 'text-slate-500 hover:text-slate-900 border-transparent',
  ].join(' ');

const PublisherSettingsLayout: React.FC = () => {
  const ctx = useSingleStationContext();
  const { slug, config, user } = ctx;
  const isManagerPlus = rankOf(user?.rawRole) >= rankOf('manager');
  // CTV-776 (payoutsFeature.ts): the whole publisher money surface is hidden
  // until Stripe Connect is live, so in production Station settings shows the
  // Team tab alone and the readiness lookup is skipped.
  const payoutsVisible = publisherBillingEnabled();
  const { needsSetup } = usePayoutSetupStatus(
    scopeForContext(ctx),
    isManagerPlus && payoutsVisible,
  );

  // One gate for both tabs (CTV-680: manager+ configures the station). The
  // redirect keeps a rep who deep-links from hitting a wall of failed
  // requests, and it fires before any tab renders.
  if (!isManagerPlus) return <Navigate to={`/admin/${slug}`} replace />;

  const stationName = displayedName(config);

  return (
    <div className="max-w-5xl">
      <AdminPageHeader
        title="Settings"
        scopeMode="bound"
        currentSlug={slug}
        subtitle={
          payoutsVisible
            ? `Who can reach ${stationName} and where its payouts go.`
            : `Who can reach ${stationName}.`
        }
      />

      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex gap-6" aria-label="Settings sections">
          <NavLink to="team" className={tabClass}>
            Team
          </NavLink>
          {payoutsVisible && (
            <NavLink to="payout-method" className={tabClass}>
              Payout method
              {/* CTV-743's dot vocabulary, repeated on the tab: the sidebar
                  dot says "something in Settings needs you", this one says
                  which tab. */}
              {needsSetup && (
                <span
                  data-testid="payout-tab-alert"
                  aria-label="Payouts not set up"
                  className="w-[10px] h-[10px] rounded-full bg-red-500"
                />
              )}
            </NavLink>
          )}
        </nav>
      </div>

      {/* Context must be forwarded: a bare <Outlet /> blanks the outlet
          context, and useSingleStationContext() in the tab would throw. */}
      <Outlet context={ctx} />
    </div>
  );
};

export default PublisherSettingsLayout;
